import { MenuItemType, SubMenuType } from "antd/es/menu/interface";
import classNames from "classnames";
import type { MouseEventHandler } from "react";
import { Link, Location, To } from "react-router";
import { CompetenceClickCheck } from "../../app/contexts/CompetenceCheckContext";
import { FinancialSector } from "../../modules/agent/enums";
import { TopAgentType } from "../enums";
import { pageRulePermissionsMap } from "../security/constants";
import { Permission } from "../security/enums";
import { hasAllPermissions, hasAnyPermission } from "../security/utils";

export const EXTERNAL_KEY_SUFFIX = "-external";

interface MenuItemConfigProps {
  externalPath?: boolean;
  search?: string;
  topLevelMenuItem?: boolean;
}

interface MenuItemProps extends Omit<MenuItemType, "key"> {
  path: string;
  key?: React.Key;
  topAgent?: TopAgentType;
  permissions: Permission[];
  routerLocation: Location;
  configProps?: MenuItemConfigProps;
  competenceCheck?: FinancialSector;
}

export const createMenuItem = ({
  path,
  label,
  permissions,
  routerLocation,
  configProps,
  topAgent,
  competenceCheck,
  ...itemProps
}: MenuItemProps): MenuItemType | undefined => {
  const renderLabel = (onClickCapture?: MouseEventHandler<HTMLElement>) =>
    configProps?.externalPath ? (
      <a href={path} target="_blank" rel="noreferrer" onClickCapture={onClickCapture}>
        {label}
      </a>
    ) : (
      <Link to={resolveLocationDescriptor(path, routerLocation, configProps?.search)} onClickCapture={onClickCapture}>
        {label}
      </Link>
    );

  const labelComponent = competenceCheck ? (
    <CompetenceClickCheck sector={competenceCheck}>{renderLabel}</CompetenceClickCheck>
  ) : (
    renderLabel()
  );

  return checkHasPathAccess(path, permissions, topAgent)
    ? {
        ...itemProps,
        key: (itemProps.key || path) + (configProps?.externalPath ? EXTERNAL_KEY_SUFFIX : ""),
        className: classNames(itemProps.className),
        label: labelComponent,
      }
    : undefined;
};

interface SubMenuItemProps extends Omit<SubMenuType, "key"> {
  path: string;
  key?: string;
  topAgent?: TopAgentType;
  permissions: Permission[];
}

export const createSubMenuItem = ({
  path,
  permissions,
  topAgent,
  ...subMenuProps
}: SubMenuItemProps): SubMenuType | undefined => {
  return checkHasPathAccess(path, permissions, topAgent)
    ? { ...subMenuProps, key: subMenuProps.key || path }
    : undefined;
};

export const checkHasPathAccess = (path: string, permissions: Permission[], topAgent?: TopAgentType) => {
  const permissionsObject = pageRulePermissionsMap.get(path);

  if (!permissionsObject) {
    return true;
  }

  if (
    (permissionsObject.allowedTopAgents && !topAgent) ||
    (permissionsObject.allowedTopAgents && topAgent && !permissionsObject.allowedTopAgents.includes(topAgent))
  ) {
    return false;
  }

  return permissionsObject.isAnyPermissionRequired
    ? hasAnyPermission(permissions, permissionsObject.permissions)
    : hasAllPermissions(permissions, permissionsObject.permissions);
};

const resolveLocationDescriptor = (pathname: string, routerLocation: Location, search?: string): To => ({
  pathname,
  search: search ? search : routerLocation.pathname === pathname ? routerLocation.search : undefined,
});
